import React, { useState, useEffect, useMemo } from 'react';
import { VideoRequest, Provider, VideoSettings } from '../types/video';
import ErrorAlert from './ErrorAlert';
import SuccessCelebration from './SuccessCelebration';
import { downloadVideo, shareVideo, downloadThumbnail } from '../utils/videoUtils';

type PreviewStatus = 'idle' | 'loading' | 'ready' | 'error';
type ViewMode = 'preview' | 'video';

interface UnifiedOutputPaneProps {
  // View mode control
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;

  // Preview props
  previewStatus: PreviewStatus;
  previewImage?: string;
  previewPrompt?: string;
  isGeneratingPreview: boolean;
  onRegeneratePreview?: () => void;

  // Video props
  video: VideoRequest | null;
  onRetry?: () => void;
  settings: VideoSettings;
  selectedProvider?: Provider;
  quality: 'fast' | 'quality';
  onAspectRatioChange?: (aspectRatio: string) => void;
  history: VideoRequest[];
  historyLoading?: boolean;
  onSelectHistory?: (video: VideoRequest) => void;
  onShowHistory?: () => void;
  previewImageData?: string;
}

const STATUS_LABELS: Record<VideoRequest['status'], string> = {
  pending: 'In wachtrij',
  processing: 'Bezig met genereren',
  completed: 'Voltooid',
  failed: 'Mislukt',
};

const STATUS_CONFIG: Record<VideoRequest['status'], { badge: string; icon: string }> = {
  pending: { badge: 'status-generating', icon: '⏳' },
  processing: { badge: 'status-generating', icon: '🔄' },
  completed: { badge: 'status-completed', icon: '✅' },
  failed: { badge: 'status-error', icon: '❌' },
};

const QUALITY_COPY: Record<'fast' | 'quality', string> = {
  fast: 'Snelle modus',
  quality: 'Hoge kwaliteit',
};

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) return 'Zojuist';
  if (diffInMinutes < 60) return `${diffInMinutes} min geleden`;
  if (diffInHours < 24) return `${diffInHours} uur geleden`;
  if (diffInDays === 1) return 'Gisteren';
  if (diffInDays < 7) return `${diffInDays} dagen geleden`;
  return date.toLocaleDateString('nl-NL');
};

const UnifiedOutputPane: React.FC<UnifiedOutputPaneProps> = ({
  mode,
  onModeChange,
  previewStatus,
  previewImage,
  previewPrompt,
  isGeneratingPreview,
  onRegeneratePreview,
  video,
  onRetry,
  settings,
  selectedProvider,
  quality,
  onAspectRatioChange,
  history,
  historyLoading = false,
  onSelectHistory,
  onShowHistory,
  previewImageData,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (video?.status === 'completed') {
      setShowCelebration(true);
      const timer = setTimeout(() => setShowCelebration(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [video?.status, video?.id]);

  const activeProvider = useMemo(() => {
    if (video?.provider) return video.provider;
    return selectedProvider;
  }, [video?.provider, selectedProvider]);

  const activeAspectRatio = useMemo(() => {
    return video?.settings?.aspectRatio || settings.aspectRatio || '9:16';
  }, [video?.settings?.aspectRatio, settings.aspectRatio]);

  const aspectRatioStyle = useMemo(() => {
    const [w, h] = activeAspectRatio.split(':').map(Number);
    if (!w || !h) return undefined;
    return `${w} / ${h}`;
  }, [activeAspectRatio]);

  const recentHistory = useMemo(() => history.slice(0, 3), [history]);

  const summaryItems = useMemo(() => [
    {
      label: 'Status',
      value: video ? STATUS_LABELS[video.status] : 'Geen actieve aanvraag',
    },
    {
      label: 'Provider',
      value: activeProvider ? activeProvider.toUpperCase() : 'Automatisch',
    },
    {
      label: 'Kwaliteit',
      value: QUALITY_COPY[quality],
    },
    {
      label: 'Lengte',
      value: `${settings.durationSeconds}s`,
    },
    {
      label: 'Kosten',
      value: video?.actualCost ? `$${video.actualCost.toFixed(2)}` : '—',
    },
    {
      label: 'Referentie',
      value: previewImageData ? 'Preview actief' : 'Niet ingesteld',
    },
    {
      label: 'Formaat',
      value: activeAspectRatio === '9:16' ? 'Portret 9:16' : 'Landschap 16:9',
    },
  ], [video, activeProvider, quality, settings.durationSeconds, activeAspectRatio, previewImageData]);

  const onAspectRatioToggle = (newAspectRatio: string) => {
    if (onAspectRatioChange) {
      onAspectRatioChange(newAspectRatio);
    }
  };

  const handleDownload = async () => {
    if (!video?.videoUrl) return;
    setIsDownloading(true);
    try {
      await downloadVideo(video.videoUrl, `vidgentf-${video.id}.mp4`);
    } catch (error: any) {
      alert(error.message || 'Download mislukt');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!video?.videoUrl) return;
    setIsSharing(true);
    try {
      await shareVideo(
        video.videoUrl,
        'VidGenTF Video',
        `Verkeerssituatie: ${video.scenarioDescription.substring(0, 50)}...`
      );
    } catch (error: any) {
      alert(error.message || 'Delen mislukt');
    } finally {
      setIsSharing(false);
    }
  };

  const handleThumbnail = async () => {
    if (!video?.videoUrl) return;
    setIsGeneratingThumbnail(true);
    try {
      await downloadThumbnail(video.videoUrl, `vidgentf-thumbnail-${video.id}.jpg`);
    } catch (error: any) {
      alert(error.message || 'Thumbnail generatie mislukt');
    } finally {
      setIsGeneratingThumbnail(false);
    }
  };

  const renderHistorySection = () => {
    const baseWrapperClasses =
      'rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-alt)] p-4';

    if (historyLoading) {
      return (
        <div className={baseWrapperClasses}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">📚</span>
              <span className="text-sm font-semibold text-[var(--color-text-primary)]">Recente video&apos;s</span>
            </div>
          </div>
          <div className="space-y-2">
            {[1, 2, 3].map((idx) => (
              <div
                key={idx}
                className="h-12 rounded-md bg-[var(--color-muted)] animate-pulse"
              />
            ))}
          </div>
        </div>
      );
    }

    if (!history.length) {
      return (
        <div className={baseWrapperClasses}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">📚</span>
              <span className="text-sm font-semibold text-[var(--color-text-primary)]">Recente video&apos;s</span>
            </div>
            {onShowHistory && (
              <button
                type="button"
                className="text-xs font-medium text-[var(--color-primary)] hover:underline"
                onClick={onShowHistory}
              >
                Open geschiedenis
              </button>
            )}
          </div>
          <div className="py-6 text-center text-xs text-[var(--color-text-secondary)]">
            Nog geen video&apos;s gegenereerd. Start met een scenario om de geschiedenis te vullen.
          </div>
        </div>
      );
    }

    return (
      <div className={baseWrapperClasses}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">📚</span>
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">Recente video&apos;s</span>
          </div>
          {onShowHistory && (
            <button
              type="button"
              className="text-xs font-medium text-[var(--color-primary)] hover:underline"
              onClick={onShowHistory}
            >
              Bekijk alles
            </button>
          )}
        </div>
        <div className="space-y-3">
          {recentHistory.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelectHistory && onSelectHistory(item)}
              className="history-preview w-full px-3 py-2 focus-visible:outline-none"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                      {item.scenarioDescription}
                    </p>
                    <p className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1 flex-wrap">
                      <span>{STATUS_LABELS[item.status]}</span>
                      <span>•</span>
                      <span>{formatTimeAgo(new Date(item.createdAt))}</span>
                      <span>•</span>
                      <span>{item.provider ? item.provider.toUpperCase() : 'AUTO'}</span>
                      <span>•</span>
                      <span>{item.settings.durationSeconds}s</span>
                      {typeof item.actualCost === 'number' && (
                        <>
                          <span>•</span>
                          <span>€{item.actualCost.toFixed(2)}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <svg
                  className="w-4 h-4 text-[var(--color-text-secondary)]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderPreviewPlaceholder = () => {
    if (previewStatus === 'loading' || isGeneratingPreview) {
      return (
        <div className="flex flex-col items-center justify-center py-10 text-center space-y-3">
          <svg className="w-8 h-8 text-[var(--color-primary)] animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <p className="text-sm text-[var(--color-text-secondary)]">Preview wordt gegenereerd...</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-10 text-center space-y-3">
        <div className="w-16 h-16 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
          <span className="text-2xl">🖼️</span>
        </div>
        <p className="text-sm text-[var(--color-text-secondary)]">Nog geen preview beschikbaar.</p>
        <p className="text-xs text-[var(--color-text-secondary)] max-w-[160px]">
          Genereer eerst een afbeelding om als referentie te gebruiken bij de video.
        </p>
      </div>
    );
  };

  const renderPreviewMode = () => {
    const showImage = previewStatus === 'ready' && previewImage;
    const showError = previewStatus === 'error';

    return (
      <div className="space-y-4">
        <div className="relative border border-dashed border-[var(--color-border)] rounded-lg overflow-hidden min-h-[220px] bg-[var(--color-surface-alt)]">
          {showImage ? (
            <img src={previewImage} alt="Preview van verkeersscenario" className="w-full h-full object-cover" />
          ) : (
            renderPreviewPlaceholder()
          )}
        </div>

        {showImage && previewPrompt && (
          <div className="bg-[var(--color-surface-alt)] border border-[var(--color-border)] rounded-lg p-3">
            <span className="block text-xs uppercase tracking-wide text-[var(--color-text-secondary)] mb-1">
              Prompt
            </span>
            <p className="text-xs text-[var(--color-text-primary)] line-clamp-4">{previewPrompt}</p>
          </div>
        )}

        {showError && (
          <div className="bg-[var(--color-destructive)]/10 text-[var(--color-destructive)] text-xs rounded-md px-3 py-2">
            Preview genereren is mislukt. Probeer het opnieuw.
          </div>
        )}
      </div>
    );
  };

  const renderVideoMode = () => {
    if (!video) {
      return (
        <div className="space-y-5">
          {/* Aspect Ratio Selector */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className="text-xs text-[var(--color-text-secondary)]">Formaat:</span>
            <div className="flex gap-1 p-1 bg-[var(--color-surface-alt)] rounded-lg">
              {['9:16', '16:9'].map((ratio) => (
                <button
                  key={ratio}
                  onClick={() => onAspectRatioToggle(ratio)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    activeAspectRatio === ratio
                      ? 'bg-[var(--color-primary)] text-white shadow-sm'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                  }`}
                >
                  {ratio === '9:16' ? 'Portret (9:16)' : 'Landschap (16:9)'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <div className="w-16 h-16 bg-[var(--color-muted)] rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-[var(--color-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-[var(--color-text-secondary)] font-medium mb-4">
              Start met een scenario en genereer je eerste video
            </p>
            <div className="flex items-center gap-2 px-4 py-2 bg-[var(--color-surface-alt)] rounded-lg border border-[var(--color-border)]">
              <span className="text-sm text-[var(--color-text-secondary)]">
                Formaat: {activeAspectRatio === '9:16' ? 'Portret (9:16)' : 'Landschap (16:9)'}
              </span>
            </div>
          </div>
        </div>
      );
    }

    const progress = video.progressPercentage ?? 0;

    const renderVideoBody = () => {
      switch (video.status) {
        case 'pending':
          return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-[var(--color-muted)] rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl animate-pulse">⏳</span>
              </div>
              <div className="status-badge status-generating mb-4">
                <span>⏳</span>
                In wachtrij
              </div>
              <p className="text-[var(--color-text-secondary)] font-medium">
                Je aanvraag staat in de wachtrij
              </p>
              <p className="text-xs text-[var(--color-text-secondary)] mt-2">
                De worker start automatisch zodra er capaciteit vrij is
              </p>
            </div>
          );
        case 'processing':
          return (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
              <div className="w-16 h-16 bg-[var(--color-muted)] rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl animate-spin">🔄</span>
              </div>
              <div className="status-badge status-generating mb-4">
                <span>🔄</span>
                Bezig met genereren
              </div>
              <div>
                <p className="text-[var(--color-text-secondary)] font-medium">
                  Video wordt gegenereerd...
                </p>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                  Dit kan enkele minuten duren, afhankelijk van de provider
                </p>
              </div>
              <div className="w-full max-w-md">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">{progress}% voltooid</p>
              </div>
              {video.provider && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-alt)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
                  <span>Provider:</span>
                  <span>{video.provider.toUpperCase()}</span>
                </div>
              )}
            </div>
          );
        case 'failed':
          return (
            <div className="py-4">
              <ErrorAlert
                errorMessage={video.errorMessage || 'Er is een onverwachte fout opgetreden'}
                onRetry={onRetry}
              />
            </div>
          );
        case 'completed':
        default:
          const statusConfig = STATUS_CONFIG[video.status];
          return (
            <div>
              <SuccessCelebration show={showCelebration} />
              <div className="mb-4">
                <div className="status-badge status-completed mb-4">
                  <span>{statusConfig.icon}</span>
                  {STATUS_LABELS[video.status]}
                </div>
                <div
                  className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-black"
                  style={aspectRatioStyle ? { aspectRatio: aspectRatioStyle } : undefined}
                >
                  {video.videoUrl ? (
                    <video key={video.videoUrl} controls autoPlay className="w-full h-full" src={video.videoUrl}>
                      <source src={video.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-white min-h-[200px]">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                          <span className="text-xl">✅</span>
                        </div>
                        <p className="text-sm">Video gereed</p>
                      </div>
                    </div>
                  )}
                </div>
                {previewImageData && (
                  <div className="mt-4">
                    <span className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">Referentiebeeld gebruikt</span>
                    <div className="mt-2 border border-dashed border-[var(--color-border)] rounded-lg overflow-hidden max-w-xs">
                      <img src={previewImageData} alt="Referentiebeeld gebruikt voor deze video" className="w-full h-full object-cover" />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="btn-secondary flex-1 min-w-[140px] disabled:opacity-50"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isDownloading ? 'Downloaden...' : 'Download'}
                </button>
                <button
                  onClick={handleShare}
                  disabled={isSharing}
                  className="btn-secondary flex-1 min-w-[140px] disabled:opacity-50"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  {isSharing ? 'Delen...' : 'Delen'}
                </button>
                <button
                  onClick={handleThumbnail}
                  disabled={isGeneratingThumbnail}
                  className="btn-secondary flex-1 min-w-[140px] disabled:opacity-50"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {isGeneratingThumbnail ? 'Genereren...' : 'Thumbnail'}
                </button>
              </div>
            </div>
          );
      }
    };

    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 flex-wrap justify-between">
          {/* Status Badge */}
          <div className={`status-badge ${STATUS_CONFIG[video.status].badge}`}>
            <span>{STATUS_CONFIG[video.status].icon}</span>
            {STATUS_LABELS[video.status]}
          </div>

          {/* Aspect Ratio Selector */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-[var(--color-text-secondary)]">Formaat:</span>
            <div className="flex gap-1 p-1 bg-[var(--color-surface-alt)] rounded-lg">
              {['9:16', '16:9'].map((ratio) => (
                <button
                  key={ratio}
                  onClick={() => onAspectRatioToggle(ratio)}
                  disabled={video.status === 'processing'}
                  className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                    activeAspectRatio === ratio
                      ? 'bg-[var(--color-primary)] text-white shadow-sm'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {ratio === '9:16' ? 'Portret (9:16)' : 'Landschap (16:9)'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {renderVideoBody()}

        {video.status !== 'failed' && (
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-alt)] p-4">
            <div className="grid gap-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {summaryItems.map(item => (
                  <div key={item.label}>
                    <span className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">{item.label}</span>
                    <div className="text-sm font-semibold text-[var(--color-text-primary)]">{item.value}</div>
                  </div>
                ))}
              </div>
              <div>
                <span className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">Scenario</span>
                <p className="mt-1 text-sm text-[var(--color-text-primary)] line-clamp-2">
                  {video.scenarioDescription}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card p-6 space-y-5">
      {/* Header with mode toggle */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Mode Toggle */}
          <div className="flex gap-1 p-1 bg-[var(--color-surface-alt)] rounded-lg border border-[var(--color-border)]">
            <button
              onClick={() => onModeChange('preview')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                mode === 'preview'
                  ? 'bg-[var(--color-primary)] text-white shadow-sm'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              Beeld
            </button>
            <button
              onClick={() => onModeChange('video')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                mode === 'video'
                  ? 'bg-[var(--color-primary)] text-white shadow-sm'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              Video
            </button>
          </div>
        </div>

        {/* Regenerate button for preview mode */}
        {mode === 'preview' && onRegeneratePreview && previewStatus === 'ready' && (
          <button
            type="button"
            onClick={onRegeneratePreview}
            disabled={isGeneratingPreview}
            className="text-xs text-[var(--color-primary)] hover:underline disabled:opacity-50"
          >
            Opnieuw
          </button>
        )}
      </div>

      {/* Content area */}
      {mode === 'preview' ? renderPreviewMode() : renderVideoMode()}

      {/* History section (shown in both modes) */}
      {renderHistorySection()}
    </div>
  );
};

export default UnifiedOutputPane;
